import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import { parseJsonRecord, readToolResultText } from '../../../contracts/toolResult';
import { isSilentCanvasRoutineTool } from '../../../contracts/workflows/lib/routineToolNames';
import { artifactCanvasKey } from '../artifacts/artifactCanvasKey';
import { isGenericSessionArtifact } from '../artifacts/sessionArtifactFocus';
import type { Artifact } from '../artifacts/state';

type ArtifactFieldUpdate = {
  token: string;
  fields: ReadonlySet<string>;
} | null;

/**
 * What the app supplies: the artifact deriver bound to the app's ports, and the
 * cache reactions, which reach other domains' caches. Pass one module-level
 * object, so each member keeps one identity across renders.
 */
export interface ProjectArtifactAutoOpenPorts {
  /** The canvas artifact a completed tool call produced, if any. */
  artifactFromToolUse: (item: ToolUseItem) => Artifact | undefined;
  /** Invalidates the caches behind an artifact, so the canvas shows the agent's change. */
  refreshArtifactData: (artifact: Artifact, queryClient: QueryClient) => void;
  /** Whether a tool is an agent write whose result dirties caches. */
  isAgentToolWriteRequiringCacheRefresh: (toolName: string) => boolean;
  /**
   * Refreshes the caches a settled agent write dirtied. `onSessionsChanged` is
   * the caller's session-list refresh, handed through.
   */
  onAgentToolWrite: (
    item: ToolUseItem,
    queryClient: QueryClient,
    onSessionsChanged: (() => void) | undefined
  ) => void;
}

interface UseProjectArtifactAutoOpenArgs {
  items: ChatItem[];
  isLoading: boolean;
  isLetterArtifactEnabled: boolean;
  isLetterArtifactAccessPending?: boolean;
  /**
   * Focused-pane mode (`release-scribe-work-session-artifact-v2`): a tool
   * result that names the WHOLE session (`create_session`, `get_session`,
   * `merge_sessions`, an `open_artifact` with no tab) never auto-opens. Only a
   * specific surface — a Document, Note, Context or Transcript, or a non-session
   * artifact — may take the canvas. The card keeps its click affordance, which
   * the canvas host turns into full Session-page navigation.
   */
  suppressGenericSessionAutoOpen?: boolean;
  turnInFlight: boolean;
  openArtifact: Artifact | null;
  userArtifactRevision: number;
  queryClient: QueryClient;
  setArtifactFieldUpdate: (update: ArtifactFieldUpdate) => void;
  setOpenArtifact: (artifact: Artifact) => void;
  /**
   * Fired where this hook used to call Session's own `invalidateSessionsQuery`
   * command directly. The session list is Session's cache, not Projects', and
   * its invalidation is version-branched inside `@heidi/session/data` — so the
   * command stays Session's and the composition point sequences it (delivery
   * §7: within a data grain a foreign domain contributes only inert
   * `/contracts`; foreign commands are composed in the consuming UI). Optional
   * on purpose: a caller that does not own a session list supplies nothing and
   * the by-id refresh below still runs.
   */
  onSessionsChanged?: () => void;
}

/**
 * A generic session read carries less navigation intent than an earlier
 * session target. Agent workflows commonly create or focus a session, then
 * hydrate its metadata with `get_session`; that read must refresh caches
 * without replacing Context/Transcript/Note/Document with the V1 default.
 */
const shouldRetainFocusedSessionArtifact = (
  current: Artifact | null,
  candidate: Artifact
): boolean =>
  current?.type === 'session' &&
  current.focusTab !== undefined &&
  candidate.type === 'session' &&
  candidate.focusTab === undefined &&
  current.sessionId === candidate.sessionId;

const objectKey = (artifact: Artifact): string => {
  if (artifact.type === 'session' && artifact.focusDocumentId) {
    return `document:${artifact.sessionId}:${artifact.focusDocumentId}`;
  }
  return artifact.type === 'session' || artifact.type === 'note'
    ? `session:${artifact.sessionId}`
    : artifactCanvasKey(artifact);
};

type ProducedArtifact = { artifact: Artifact; resultText?: string };
type TurnArtifacts = { token: string; produced: ProducedArtifact[] };

/**
 * When a turn ends, refreshes every artifact it produced; if it produced exactly
 * one, opens it in a closed canvas (or refreshes it in place when it is already
 * the one showing) and highlights only the fields the backend says changed.
 * Nothing opens while the turn runs, for a turn that produced several artifacts,
 * or over anything the clinician opened or closed during the turn.
 * Also refreshes react-query caches when agent write tools settle so an
 * already-open canvas updates without stealing focus.
 *
 * The ports are module-level at the app, so listing them as dependencies fires
 * nothing the arguments would not.
 */
export const useProjectArtifactAutoOpen = (
  {
    items,
    isLoading,
    isLetterArtifactEnabled,
    isLetterArtifactAccessPending = false,
    suppressGenericSessionAutoOpen = false,
    turnInFlight,
    openArtifact,
    userArtifactRevision,
    queryClient,
    setArtifactFieldUpdate,
    setOpenArtifact,
    onSessionsChanged,
  }: UseProjectArtifactAutoOpenArgs,
  {
    artifactFromToolUse,
    refreshArtifactData,
    isAgentToolWriteRequiringCacheRefresh,
    onAgentToolWrite,
  }: ProjectArtifactAutoOpenPorts
) => {
  const turnArtifacts = useMemo<TurnArtifacts | null>(() => {
    let turnStart = -1;
    items.forEach((item, index) => {
      if (item.kind === 'user_message') turnStart = index;
    });
    const produced = new Map<string, ProducedArtifact>();
    let token: string | null = null;
    for (const item of items.slice(turnStart + 1)) {
      if (item.kind === 'turn_cancelled_marker' || item.kind === 'turn_failed') {
        return null;
      }
      if (item.kind !== 'tool_use') continue;
      if (item.status !== 'complete' || item.result?.is_error) continue;
      // Schedule / trigger / share derive a workflow artifact for the Open
      // card but must not steal canvas focus — they refresh an already-open
      // canvas instead.
      if (isSilentCanvasRoutineTool(item.name)) continue;
      // `get_letter` is opened only through the explicit action on its timeline
      // card; completing a read must not steal focus from the current artifact.
      if (item.name === 'get_letter') continue;
      const artifact = artifactFromToolUse(item);
      if (!artifact) continue;
      if (
        artifact.type === 'letter' &&
        !isLetterArtifactEnabled &&
        !isLetterArtifactAccessPending
      ) {
        continue;
      }
      if (suppressGenericSessionAutoOpen && isGenericSessionArtifact(artifact)) continue;
      const retained = [...produced.values()].some((earlier) =>
        shouldRetainFocusedSessionArtifact(earlier.artifact, artifact)
      );
      if (retained) continue;
      produced.set(objectKey(artifact), {
        artifact,
        resultText: readToolResultText(item),
      });
      token = item.id;
    }
    return token === null ? null : { token, produced: [...produced.values()] };
  }, [
    artifactFromToolUse,
    isLetterArtifactAccessPending,
    isLetterArtifactEnabled,
    items,
    suppressGenericSessionAutoOpen,
  ]);

  const latestAgentWrite = useMemo<{ token: string; item: ToolUseItem } | null>(() => {
    let latest: { token: string; item: ToolUseItem } | null = null;
    for (const item of items) {
      if (item.kind !== 'tool_use') continue;
      if (item.status !== 'complete' || item.result?.is_error) continue;
      if (!isAgentToolWriteRequiringCacheRefresh(item.name)) continue;
      latest = { token: item.id, item };
    }
    return latest;
  }, [isAgentToolWriteRequiringCacheRefresh, items]);

  const autoOpenedTokenRef = useRef<string | null>(null);
  const hasPrimedArtifactRef = useRef(false);
  const revisionAtTurnStartRef = useRef(userArtifactRevision);
  const wasTurnInFlightRef = useRef(false);
  const refreshedAgentWriteTokenRef = useRef<string | null>(null);
  const hasPrimedAgentWriteRef = useRef(false);

  useEffect(() => {
    if (turnInFlight && !wasTurnInFlightRef.current) {
      revisionAtTurnStartRef.current = userArtifactRevision;
    }
    wasTurnInFlightRef.current = turnInFlight;
  }, [turnInFlight, userArtifactRevision]);

  useEffect(() => {
    if (isLoading) return;
    if (!hasPrimedArtifactRef.current) {
      hasPrimedArtifactRef.current = true;
      // A turn already running at mount is live; a finished one is history.
      autoOpenedTokenRef.current = turnInFlight ? null : (turnArtifacts?.token ?? null);
    }
    if (turnInFlight) return;
    if (!turnArtifacts) return;
    const { token, produced } = turnArtifacts;
    if (autoOpenedTokenRef.current === token) return;
    if (
      isLetterArtifactAccessPending &&
      produced.some(({ artifact }) => artifact.type === 'letter')
    ) {
      return;
    }
    autoOpenedTokenRef.current = token;
    for (const { artifact } of produced) refreshArtifactData(artifact, queryClient);
    const [only, ...rest] = produced;
    if (!only || rest.length > 0) return;
    if (revisionAtTurnStartRef.current !== userArtifactRevision) return;
    const refreshesInPlace =
      openArtifact !== null &&
      artifactCanvasKey(openArtifact) === artifactCanvasKey(only.artifact);
    if (openArtifact !== null && !refreshesInPlace) return;

    setOpenArtifact(only.artifact);
    setArtifactFieldUpdate(parseUpdatedFields(token, only.resultText));
  }, [
    isLoading,
    isLetterArtifactAccessPending,
    turnInFlight,
    turnArtifacts,
    openArtifact,
    setOpenArtifact,
    setArtifactFieldUpdate,
    queryClient,
    refreshArtifactData,
    userArtifactRevision,
  ]);

  useEffect(() => {
    if (isLoading) return;
    if (!hasPrimedAgentWriteRef.current) {
      hasPrimedAgentWriteRef.current = true;
      refreshedAgentWriteTokenRef.current = latestAgentWrite?.token ?? null;
      return;
    }
    if (!latestAgentWrite) return;
    if (refreshedAgentWriteTokenRef.current === latestAgentWrite.token) return;
    refreshedAgentWriteTokenRef.current = latestAgentWrite.token;

    onAgentToolWrite(latestAgentWrite.item, queryClient, onSessionsChanged);
  }, [isLoading, latestAgentWrite, queryClient, onAgentToolWrite, onSessionsChanged]);
};

const parseUpdatedFields = (
  token: string,
  resultText: string | undefined
): ArtifactFieldUpdate => {
  // Tolerant parse: an approved call's result can carry a line after its JSON.
  const updatedFields = parseJsonRecord(resultText)?.updated_fields;
  if (!Array.isArray(updatedFields)) return null;
  const fields = updatedFields.filter(
    (f): f is string => typeof f === 'string' && f.length > 0
  );
  return fields.length > 0 ? { token, fields: new Set(fields) } : null;
};
